import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocalizedPath } from "../../i18n/localizedPath";
import styles from "./conjugationPage.module.scss";

// Strings in conjugationPage.json mark their Japanese with <ja>…</ja>, so the
// browser and screen readers get lang="ja" on every run of it.
export const JA_TRANS_COMPONENTS = { ja: <span lang="ja" className={styles.teIruJa} /> };

interface GuideExample {
    japanese: string;
    reading: string;
    // Key under teIru.guide.examples in conjugationPage.json.
    glossKey: string;
}

interface GuideUse {
    id: "ongoing" | "habitual" | "result";
    examples: readonly GuideExample[];
}

// Japanese and its reading are language-neutral, so they live here; only the
// glosses are translated. Every sentence is plain (casual) so it matches the
// first row of the ている group on each verb card.
const GUIDE_USES: readonly GuideUse[] = [
    {
        id: "ongoing",
        examples: [
            { japanese: "食べている", reading: "たべている", glossKey: "eating" },
            { japanese: "今、本を読んでいる。", reading: "いま、ほんをよんでいる。", glossKey: "readingBook" },
        ],
    },
    {
        id: "habitual",
        examples: [
            { japanese: "毎朝走っている。", reading: "まいあさはしっている。", glossKey: "runEveryMorning" },
            {
                japanese: "毎日日本語を勉強している。",
                reading: "まいにちにほんごをべんきょうしている。",
                glossKey: "studyEveryDay",
            },
        ],
    },
    {
        id: "result",
        examples: [
            { japanese: "結婚している", reading: "けっこんしている", glossKey: "married" },
            { japanese: "住んでいる", reading: "すんでいる", glossKey: "lives" },
            { japanese: "持っている", reading: "もっている", glossKey: "has" },
            { japanese: "知っている", reading: "しっている", glossKey: "knows" },
            { japanese: "着ている", reading: "きている", glossKey: "wearing" },
            { japanese: "行っている", reading: "いっている", glossKey: "gone" },
            { japanese: "来ている", reading: "きている", glossKey: "come" },
            { japanese: "彼は結婚している。", reading: "かれはけっこんしている。", glossKey: "heIsMarried" },
        ],
    },
];

const GUIDE_TIPS = ["goKuru", "shiru", "notYet", "contraction", "stative"] as const;

// A closed-by-default explanation, so the filters stay near the top of a phone
// screen; <details> is keyboard operable and announced without any script.
function TeIruGuide(): React.ReactElement {
    const { t } = useTranslation("conjugationPage");
    const lp = useLocalizedPath();

    return (
        <details className={`${styles.card} ${styles.teIruGuide}`}>
            <summary className={styles.teIruGuideSummary}>
                <Trans t={t} i18nKey="teIru.guide.summary" components={JA_TRANS_COMPONENTS} />
            </summary>
            <div className={styles.teIruGuideBody}>
                <p className={styles.teIruGuideText}>
                    <Trans t={t} i18nKey="teIru.guide.intro" components={JA_TRANS_COMPONENTS} />
                </p>
                <ul className={styles.teIruUseList}>
                    {GUIDE_USES.map((use) => (
                        <li key={use.id} className={styles.teIruUse}>
                            <p className={styles.teIruUseTitle}>{t(`teIru.guide.uses.${use.id}.title`)}</p>
                            <p className={styles.teIruGuideText}>{t(`teIru.guide.uses.${use.id}.text`)}</p>
                            <ul className={styles.teIruExamples}>
                                {use.examples.map((example) => (
                                    <li key={example.japanese}>
                                        <span lang="ja" className={styles.teIruExampleJa}>
                                            {example.japanese}
                                        </span>{" "}
                                        <span lang="ja" className={styles.teIruExampleReading}>
                                            ({example.reading})
                                        </span>{" "}
                                        — {t(`teIru.guide.examples.${example.glossKey}`)}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
                <ul className={styles.teIruTips}>
                    {GUIDE_TIPS.map((tip) => (
                        <li key={tip} className={styles.teIruGuideText}>
                            <Trans t={t} i18nKey={`teIru.guide.tips.${tip}`} components={JA_TRANS_COMPONENTS} />
                        </li>
                    ))}
                </ul>
                <p className={styles.teIruGuideText}>
                    <Link to={lp("/grammar/n5-teimasu-continuous")}>
                        <Trans t={t} i18nKey="teIru.guide.grammarLink" components={JA_TRANS_COMPONENTS} />
                    </Link>
                </p>
            </div>
        </details>
    );
}

export default TeIruGuide;
